import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Button, Form, Input, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import InvitationService, { InvitationValidationResult } from '../../services/InvitationService';
import logo from '../../assets/images/logo.png';

const AcceptInvitation: FunctionComponent = () => {
    const history = useHistory();
    const location = useLocation();
    const token = new URLSearchParams(location.search).get('token') || '';

    const [validating, setValidating] = useState(true);
    const [validation, setValidation] = useState<InvitationValidationResult | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setValidation({ isValid: false, errorMessage: 'No invitation token found in the link.', email: '', fullName: '' });
            setValidating(false);
            return;
        }
        InvitationService.validate(token)
            .then((result) => setValidation(result))
            .catch(() => setValidation({ isValid: false, errorMessage: 'Unable to verify invitation. Please try again.', email: '', fullName: '' }))
            .finally(() => setValidating(false));
    }, [token]);

    const onFinish = async (values: { password: string; confirm: string }) => {
        if (values.password !== values.confirm) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await InvitationService.accept(token, values.password);
            setSuccess(true);
            setTimeout(() => history.push('/login'), 3000);
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Something went wrong. Please try again.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #0A1E36 0%, #12304D 100%)',
                padding: '24px',
            }}
        >
            <div
                style={{
                    width: 420,
                    padding: '40px 32px',
                    borderRadius: 12,
                    background: '#fff',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                }}
            >
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <img src={logo} alt="Virgate" style={{ width: 150, height: 'auto' }} />
                </div>

                {validating && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Spin />
                        <p style={{ marginTop: 12, color: '#8c8c8c' }}>Verifying invitation…</p>
                    </div>
                )}

                {!validating && validation && !validation.isValid && (
                    <Alert
                        type="error"
                        showIcon
                        message="Invalid Invitation"
                        description={validation.errorMessage}
                    />
                )}

                {!validating && validation?.isValid && !success && (
                    <>
                        <h2 style={{ textAlign: 'center', marginBottom: 4, fontSize: 20, color: '#1c4050' }}>
                            Set your password
                        </h2>
                        <p style={{ textAlign: 'center', color: '#8c8c8c', marginBottom: 24, fontSize: 14 }}>
                            Welcome, {validation.fullName || validation.email}. Choose a password to activate your account.
                        </p>

                        {error && (
                            <Alert
                                message={error}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                                closable
                                onClose={() => setError('')}
                            />
                        )}

                        <Form onFinish={onFinish} size="large">
                            <Form.Item
                                name="password"
                                rules={[
                                    { required: true, message: 'Please enter a password.' },
                                    { min: 8, message: 'Password must be at least 8 characters.' },
                                ]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="New password" />
                            </Form.Item>
                            <Form.Item
                                name="confirm"
                                rules={[{ required: true, message: 'Please confirm your password.' }]}
                            >
                                <Input.Password prefix={<LockOutlined />} placeholder="Confirm password" />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitting}
                                    block
                                    style={{
                                        height: 44,
                                        borderRadius: 8,
                                        fontWeight: 500,
                                        background: '#1A60A6',
                                        borderColor: '#1A60A6',
                                    }}
                                >
                                    Set Password & Continue
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                )}

                {success && (
                    <Alert
                        type="success"
                        showIcon
                        message="Password set!"
                        description="Your account is ready. Redirecting you to login…"
                    />
                )}
            </div>
        </div>
    );
};

export default AcceptInvitation;
